import { v1 } from 'uuid';
import { hasOwnProperty } from '@/utils/misc';

export default class MultiVectorLayer {
    constructor(group, zoomRange) {
        this._map = null;
        this._id = v1();
        this._source = null;
        this._group = group;
        this._zoomRange = zoomRange;
    }

    get id() {
        return ['polygon', 'line', 'point'].map(name => {
            return this._id + '.' + name;
        });
    }
    get sourceId() {
        return this._id + '.' + 'source';
    }

    eachLayer(callback) {
        if (this._map) {
            ['polygon', 'line', 'point'].forEach((name, index) => {
                const layer = this._map.getLayer(this._id + '.' + name);
                if (layer) {
                    callback(layer, index);
                }
            });
        }
    }

    /**
     * 设置样式
     * @param {Object} styles
     */
    set Styles(styles) {
        for (const key in styles) {
            const id = this._id + '.' + key;
            if (this._map && this._map.getLayer(id)) {
                const _styles = styles[key];
                // 添加样式
                for (const styleKey in _styles) {
                    if (hasOwnProperty(_styles, styleKey)) {
                        const value = _styles[styleKey];
                        this._map.setPaintProperty(id, styleKey, value);
                    }
                }
            }
        }
    }

    /**
     * 获取Features
     */
    get Features() {
        const features = this._source._data.features;
        return JSON.parse(JSON.stringify(features));
    }

    /**
     * 获取Bounds
     */
    get Bounds() {
        const features = this.Features;
        const len = features.length;
        const geometries = new Array(len);
        for (let i = 0; i < len; ++i) {
            geometries[i] = GeoGlobe.Feature.fromGeoJson(
                features[i]
            )[0].geometry;
        }
        const geom = new GeoGlobe.Geometry.Collection(geometries);
        geom.calculateBounds();
        return geom.bounds;
    }

    /**
     * 根据ID查询(id为自定义id)
     * @param {*} id
     * @returns {Array}
     */
    get(id) {
        const filterFeatures = this.Features.filter(
            f => f.id === id || f.properties.id === id
        );
        return filterFeatures;
    }

    /**
     * 添加feature
     * @param  {...any} rest
     */
    add(...rest) {
        this.setData([...this.Features, ...rest]);
    }

    /**
     * 删除Feature
     * @param {*} id
     */
    remove(id) {
        const filterFeatures = this.Features.filter(f => f.id !== id);
        this.setData(filterFeatures);
    }

    /**
     * 清空
     */
    clear() {
        this.setData([]);
    }

    /**
     * @api
     * 设置数据
     * @param {*} features
     */
    setData(features) {
        features = JSON.parse(JSON.stringify(features));
        this._source.setData({ type: 'FeatureCollection', features: features });
    }

    setMap(map) {
        if (map) {
            this._map = map;
            // 生成source
            const sourceId = this._id + '.' + 'source';
            const source = {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: [] },
            };
            // 生成layers
            const pointlayer = {
                id: this._id + '.' + 'point',
                minzoom: this._zoomRange[0],
                maxzoom: this._zoomRange[1],
                type: 'circle',
                filter: ['all', ['==', '$type', 'Point'], ['!has', 'symbol']],
                paint: { 'circle-radius': 6 },
                source: sourceId,
            };
            const linelayer = {
                id: this._id + '.' + 'line',
                minzoom: this._zoomRange[0],
                maxzoom: this._zoomRange[1],
                type: 'line',
                source: sourceId,
            };
            const polygonlayer = {
                id: this._id + '.' + 'polygon',
                minzoom: this._zoomRange[0],
                maxzoom: this._zoomRange[1],
                type: 'fill',
                filter: ['all', ['==', '$type', 'Polygon']],
                source: sourceId,
            };
            // 添加source和layers
            map.addSource(sourceId, source);
            map.addLayer(polygonlayer, this._group);
            map.addLayer(linelayer, this._group);
            map.addLayer(pointlayer, this._group);
            // 保存源source
            this._source = map.getSource(sourceId);
        } else {
            if (this._map) {
                map = this._map;
                delete this._source;
                map.removeLayer(this._id + '.' + 'point');
                map.removeLayer(this._id + '.' + 'line');
                map.removeLayer(this._id + '.' + 'polygon');
                map.removeSource(this._id + '.' + 'source');
                delete this._map;
            }
        }
    }
}
